import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	useHistory,
	useLocation,
} from 'react-router';
import {
	useQuery,
	useQueryClient,
} from 'react-query';
import { FixtureTypes } from '@shared/utils/constants';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { CargoDetails } from '@api/features/cargos/getCargoDetails';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import type { EstimatesWithResults } from '@api/features/estimates/getEstimateGroup';
import type {
	GetFixtureDetailsResponse,
	PeriodSchedule,
} from '@api/features/fixtures/getFixtureDetails';
import type { UpdateFixtureAttributes } from '@api/features/fixtures/updateFixture';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import { UpdateTcTermsRequest } from '@api/features/fixtures/updateTcTerms';
import { TcTermsProps } from '@api/models/tc-terms';
import {
	createSubEstimate,
	deleteEstimate,
	getEstimateDetails,
	getEstimateGroup,
	getFixtureDetails,
	getVesselDetails,
} from '@client/lib/api';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import { LocalPortRotation } from './hooks/usePortRotationHandlers';

type CentralStoreContextType = {
	estimate: GetEstimateDetailsResponse | null;
	estimates: EstimatesWithResults | null;
	fixtureDetails: GetFixtureDetailsResponse<TcFixtureProps> | null;
	fixtureDetailsLoading: boolean;
	refreshFixture: () => void;
	setEstimate: React.Dispatch<React.SetStateAction<GetEstimateDetailsResponse | null>>;
	syncEstimate: boolean;
	setSyncEstimate: React.Dispatch<React.SetStateAction<boolean>>;
	selectedEstimateId: number | null;
	setSelectedEstimateId: React.Dispatch<React.SetStateAction<number | null>>;
	setEstimateGroupId: React.Dispatch<React.SetStateAction<number | null>>;
	result: GetEstimateDetailsResponse | null;
	cargos: CargoDetails[];
	setCargos: React.Dispatch<React.SetStateAction<CargoDetails[]>>;
	syncCargos: boolean;
	setSyncCargos: React.Dispatch<React.SetStateAction<boolean>>;
	portRotation: LocalPortRotation[];
	setPortRotation: React.Dispatch<React.SetStateAction<LocalPortRotation[]>>;
	syncPortRotation: 'partial' | 'full' | undefined;
	setSyncPortRotation: React.Dispatch<React.SetStateAction<'partial' | 'full' | undefined>>;
	vessel: GetVesselDetailsResponse | null | undefined;
	refreshVessel: () => void;
	refreshEverything: () => Promise<void>;
	handleCreateSubEstimate: (params?: {
		blank?: boolean;
		cargoIds?: number[];
		estimateId?: number;
	}) => Promise<number | undefined>;
	onDeleteEstimate: (id?: number) => Promise<void>;
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	refreshEstimateGroup: () => void;
	tceDiff: number;
	selectedEstimate: EstimatesWithResults[number] | undefined;
	pendingFixtureChanges: Partial<UpdateFixtureAttributes>;
	setPendingFixtureChanges: (changes: Partial<UpdateFixtureAttributes>) => void;
	onUpdateFixture: (attributes: UpdateFixtureAttributes) => void;
	onUpdateTcTerms: (attriutes: UpdateTcTermsRequest['attributes']) => void;
	pendingPeriodScheduleChanges: PeriodSchedule[];
	setPendingPeriodScheduleChanges: (schedule: PeriodSchedule[]) => void;
	tcTerms: TcTermsProps | null;
	isGroupFixed?: boolean;
};

const CentralStoreContext = createContext<CentralStoreContextType>(undefined!);
export const useCentralStore = () => useContext(CentralStoreContext);

export const CentralStoreProvider = ({ children }: { children: React.ReactNode }) => {
	const history = useHistory();
	const location = useLocation();
	const queryClient = useQueryClient();

	const [selectedEstimateId, setSelectedEstimateId] = useState<number | null>(null);
	const [estimateGroupId, setEstimateGroupId] = useState<number | null>(null);
	const [estimate, setEstimate] = useState<GetEstimateDetailsResponse | null>(null);
	const [syncEstimate, setSyncEstimate] = useState(false);
	const [loading, setLoading] = useState(false);

	const [result, setResult] = useState<GetEstimateDetailsResponse | null>(null);

	const [cargos, setCargos] = useState<CargoDetails[]>([]);
	const [syncCargos, setSyncCargos] = useState(false);

	const [portRotation, setPortRotation] = useState<LocalPortRotation[]>([]);
	const [syncPortRotation, setSyncPortRotation] = useState<'partial' | 'full'>();

	const previousTceRef = useRef<number | null>(null);

	const [tceDiff, setTceDiff] = useState<number>(0);

	const [
		pendingFixtureChanges,
		setPendingFixtureChanges,
	] = useState<Partial<UpdateFixtureAttributes>>({});

	const [pendingPeriodScheduleChanges, setPendingPeriodScheduleChanges] =
		useState<PeriodSchedule[]>([]);

	const [localFixtureDetails, setLocalFixtureDetails] = useState<
		GetFixtureDetailsResponse<TcFixtureProps> | null
	>(null);

	const [localTcTerms, setLocalTcTerms] = useState<TcTermsProps | null>(null);

	useEffect(() => {
		if (estimate != null && estimate.result != null && estimate.result.tce != null) {
			const currentTce = estimate.result.tce;
			const previousTce = previousTceRef.current;

			const diff = currentTce - (previousTce ?? 0);
			setTceDiff(diff);

			previousTceRef.current = currentTce;
		}
	}, [estimate]);

	const [
		vessel,
		refreshVessel,
	] = useFetchedState(
		async () => {
			if (estimate?.vesselId == null || estimate?.vesselId === -1) {
				return null;
			}

			return getVesselDetails(estimate.vesselId);
		},
		[estimate?.vesselId],
	);

	const {
		data: estimateGroup,
		refetch: refreshEstimateGroup,
	} = useQuery(
		['estimateGroup', estimateGroupId],
		async () => getEstimateGroup(estimateGroupId!),
		{
			retry: false,
			enabled: estimateGroupId != null,
		},
	);

	const onUpdateTcTerms = (
		attributes: UpdateTcTermsRequest['attributes'],
	) => {
		setPendingFixtureChanges((prev) => {
			return { ...prev, ...attributes } as Partial<UpdateFixtureAttributes>;
		});
		setLocalTcTerms((prev) => {
			if (prev == null) {
				return null;
			}

			return { ...prev, ...attributes };
		});

	};

	const onUpdateFixture = (
		attributes: UpdateFixtureAttributes,
	) => {
		setPendingFixtureChanges((prev) => {
			return { ...prev, ...attributes };
		});

		setLocalFixtureDetails((prev) => {
			if (prev == null) {
				return null;
			}

			return { ...prev, ...attributes };
		});
	};

	const clearFixturePendingChanges = useCallback(() => {
		setPendingFixtureChanges({});
		setPendingPeriodScheduleChanges([]);
	}, []);

	const resetFixtureLocalState = useCallback(() => {
		clearFixturePendingChanges();
		setLocalFixtureDetails(null);
		setLocalTcTerms(null);
	}, [clearFixturePendingChanges]);

	const loadFixtureDetails = useCallback(async (fixtureId: number) => {
		const details = await queryClient.fetchQuery(
			['fixtureDetails', fixtureId],
			() => getFixtureDetails(fixtureId) as Promise<GetFixtureDetailsResponse<TcFixtureProps>>,
			{ staleTime: 0 },
		);
		setLocalFixtureDetails(details);
		setLocalTcTerms(details.tcTerms);

		return details;
	}, [queryClient]);

	const {
		data: _fixtureDetails,
		isLoading: fixtureDetailsLoading,
		isFetching: fixtureDetailsFetching,
	} = useQuery(
		['fixtureDetails', estimate?.Fixture?.id],
		async () => {
			const details = await getFixtureDetails(
				estimate!.Fixture!.id!,
			) as GetFixtureDetailsResponse<TcFixtureProps>;
			setLocalFixtureDetails(details);
			setLocalTcTerms(details.tcTerms);

			return details;
		},
		{
			retry: false,
			enabled: estimate != null &&
				(
					estimate.type === FixtureTypes.TC_OUT ||
					estimate.type === FixtureTypes.BB_OUT
				) &&
				estimate.Fixture != null,
		},
	);

	const refreshFixture = useCallback(() => {
		const fixtureId = estimate?.Fixture?.id;

		if (fixtureId == null) {
			return;
		}

		void loadFixtureDetails(fixtureId);
	}, [estimate?.Fixture?.id, loadFixtureDetails]);

	const selectedEstimate = useMemo(() => {
		if (selectedEstimateId == null || estimateGroup == null) {
			return undefined;
		}

		return estimateGroup.estimates.find((e) => e.id === selectedEstimateId);
	}, [selectedEstimateId, estimateGroup]);

	const handleCreateSubEstimate = async ({
		blank,
		cargoIds,
		estimateId,
	}: {
		blank?: boolean;
		cargoIds?: number[];
		estimateId?: number;
	} = {}) => {
		if (
			estimateGroupId == null ||
			estimateGroup == null ||
			selectedEstimateId == null
		) {
			return undefined;
		}

		const createdEstimate = await createSubEstimate(selectedEstimateId, blank, cargoIds, estimateId);

		resetFixtureLocalState();
		await refreshEstimateGroup();
		setSelectedEstimateId(createdEstimate);

		return createdEstimate;
	};

	const onDeleteEstimate = async (id?: number) => {
		const targetId = id ?? selectedEstimateId;

		if (targetId == null) {
			return;
		}

		await deleteEstimate(targetId);
		showSuccessNotification('Estimate deleted');
		await refreshEstimateGroup();

		if (targetId === selectedEstimateId) {
			setSelectedEstimateId(null);
		}
	};

	const refreshCargoData = async (incomingEstimate: GetEstimateDetailsResponse) => {
		setCargos(incomingEstimate.cargos);
		setSyncCargos(true);
	};

	const refreshEstimateData = async (incomingEstimate: GetEstimateDetailsResponse) => {
		setEstimate(incomingEstimate);
		setSyncEstimate(true);
	};

	const refreshPortRotationData = async (
		incomingEstimate: GetEstimateDetailsResponse,
		sync: 'partial' | 'full',
	) => {
		setPortRotation(incomingEstimate.portRotationEntries as LocalPortRotation[]);
		setSyncPortRotation(sync);
	};

	const refreshResult = useCallback(async (incomingEstimate: GetEstimateDetailsResponse) => {
		setResult(incomingEstimate);
	}, []);

	const fetchEstimate = useCallback(async () => {
		if (!selectedEstimateId) {
			return null;
		}

		const data = await queryClient.fetchQuery(
			['estimateDetails', selectedEstimateId],
			() => getEstimateDetails(selectedEstimateId),
			{ staleTime: 0 },
		);

		return data;
	}, [queryClient, selectedEstimateId]);

	const refreshEverything = useCallback(async () => {
		const estimateDetails = await fetchEstimate();

		if (estimateDetails != null) {
			refreshCargoData(estimateDetails);
			refreshEstimateData(estimateDetails);
			refreshPortRotationData(estimateDetails, 'full');
			refreshResult(estimateDetails);
			refreshEstimateGroup();

			const fixtureId = estimateDetails.Fixture?.id;

			if (
				(
					estimateDetails.type === FixtureTypes.TC_OUT ||
					estimateDetails.type === FixtureTypes.BB_OUT
				) &&
				fixtureId != null
			) {
				await loadFixtureDetails(fixtureId);
			}
		}
	}, [fetchEstimate, refreshResult, refreshEstimateGroup, loadFixtureDetails]);

	useEffect(() => {
		resetFixtureLocalState();
	}, [selectedEstimateId, resetFixtureLocalState]);

	useEffect(() => {
		refreshEverything();
	}, [refreshEverything, selectedEstimateId]);

	useEffect(() => {
		if (
			selectedEstimateId == null &&
			estimateGroup != null
		) {
			const estimateIds = estimateGroup?.estimates?.map((e) => e.id);

			if (estimateIds == null || estimateIds.length === 0) {
				history.push('/estimates/spot');

				return;
			}

			setSelectedEstimateId(estimateIds[0]);
		}
	}, [selectedEstimateId, estimateGroup, history]);

	useEffect(() => {
		if (selectedEstimateId != null) {
			fetchEstimate().then((estimateDetails) => {
				if (estimateDetails != null) {
					setEstimate(estimateDetails);
					refreshCargoData(estimateDetails);
					refreshPortRotationData(estimateDetails, 'full');
					refreshResult(estimateDetails);
				}
			});

			const pathSegments = location.pathname.split('/');
			pathSegments[pathSegments.length - 1] = selectedEstimateId.toString();

			const newPath = pathSegments.join('/');
			history.replace(newPath);
		}
	}, [
		selectedEstimateId,
		fetchEstimate,
		refreshResult,
		history,
		estimateGroupId,
		location.pathname,
	]);

	return (
		<CentralStoreContext.Provider
			value={{
				onUpdateFixture,
				onUpdateTcTerms,
				tcTerms: localTcTerms,
				fixtureDetails: localFixtureDetails,
				fixtureDetailsLoading: fixtureDetailsLoading || fixtureDetailsFetching,
				refreshFixture,
				isGroupFixed: estimateGroup?.isGroupFixed,
				estimate,
				selectedEstimateId,
				selectedEstimate,
				estimates: estimateGroup?.estimates ?? [],
				setEstimate,
				syncEstimate,
				setSyncEstimate,
				result,
				setSelectedEstimateId,
				setEstimateGroupId,
				cargos,
				setCargos,
				syncCargos,
				setSyncCargos,
				portRotation,
				setPortRotation,
				syncPortRotation,
				setSyncPortRotation,
				vessel,
				refreshVessel,
				refreshEverything,
				handleCreateSubEstimate,
				onDeleteEstimate,
				loading,
				refreshEstimateGroup,
				setLoading,
				tceDiff,
				pendingFixtureChanges,
				setPendingFixtureChanges,
				setPendingPeriodScheduleChanges,
				pendingPeriodScheduleChanges,
			}}
		>
			{children}
		</CentralStoreContext.Provider>
	);
};
